import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { solicitudDetalleService } from '../../services';

const tipoOptions = [
  { value: 'CUIL', label: 'CUIL' },
  { value: 'CUIT', label: 'CUIT' },
  { value: 'CDI', label: 'CDI' },
  { value: 'NIF', label: 'NIF' },
  { value: 'NIE', label: 'NIE' },
  { value: 'CIF', label: 'CIF' },
  { value: 'RUT', label: 'RUT' },
  { value: 'RUN', label: 'RUN' },
  { value: 'NIT', label: 'NIT' },
  { value: 'SAT', label: 'SAT' },
  { value: 'RFC', label: 'RFC' },
  { value: 'NSS', label: 'NSS' },
  { value: 'SSN', label: 'SSN' },
  { value: 'TIN', label: 'TIN' },
  { value: 'TaxID', label: 'TaxID' },
  { value: 'CPF', label: 'CPF' },
  { value: 'DUI', label: 'DUI' },
  { value: 'RTU', label: 'RTU' },
  { value: 'Otro', label: 'Otro' }
];

const tipoIvaOptions = [
  { value: 'RESPONSABLE_INSCRIPTO', label: 'Responsable inscripto' },
  { value: 'RESPONSABLE_MONOTRIBUTO', label: 'Responsable monotributo' },
  { value: 'CONSUMIDOR_FINAL', label: 'Consumidor final' },
  { value: 'EXENTO', label: 'Exento' },
  { value: 'NO_CATEGORIZADO', label: 'No categorizado' },
  { value: 'NO_ALCANZADO', label: 'No alcanzado' }
];

const tipoGananciaOptions = [
  { value: 'INSCRIPTO', label: 'Inscripto' },
  { value: 'NO_INSCRIPTO', label: 'No inscripto' },
  { value: 'EXENTO', label: 'Exento' },
  { value: 'RESPONSABLE_MONOTRIBUTO', label: 'Responsable monotributo' }
];

const SeccionDatosFiscales = ({ solicitudId, tipo }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    tipo: 'CUIL',
    claveFiscal: '',
    tipoIva: 'RESPONSABLE_MONOTRIBUTO',
    tipoGanancia: 'RESPONSABLE_MONOTRIBUTO'
  });

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const getMethod = tipo === 'individuo'
        ? solicitudDetalleService.getDatosFiscalesIndividuo
        : solicitudDetalleService.getDatosFiscalesEmpresa;
      const response = await getMethod(solicitudId);
      if (response) {
        setFormData({
          tipo: response.tipo || 'CUIL',
          claveFiscal: response.claveFiscal || '',
          tipoIva: response.tipoIva || 'RESPONSABLE_MONOTRIBUTO',
          tipoGanancia: response.tipoGanancia || 'RESPONSABLE_MONOTRIBUTO'
        });
      }
    } catch (err) {
      setError('Error al cargar los datos fiscales');
    } finally {
      setLoading(false);
    }
  }, [solicitudId, tipo]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        ...formData
      };
      const saveMethod = tipo === 'individuo'
        ? solicitudDetalleService.saveDatosFiscalesIndividuo
        : solicitudDetalleService.saveDatosFiscalesEmpresa;
      await saveMethod(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Datos Fiscales
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo de Clave Fiscal</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  label="Tipo de Clave Fiscal"
                >
                  {tipoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Clave Fiscal"
                required
                value={formData.claveFiscal}
                onChange={(e) => handleInputChange('claveFiscal', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo IVA</InputLabel>
                <Select
                  value={formData.tipoIva}
                  onChange={(e) => handleInputChange('tipoIva', e.target.value)}
                  label="Tipo IVA"
                >
                  {tipoIvaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo Ganancia</InputLabel>
                <Select
                  value={formData.tipoGanancia}
                  onChange={(e) => handleInputChange('tipoGanancia', e.target.value)}
                  label="Tipo Ganancia"
                >
                  {tipoGananciaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDatosFiscales;

